import type { LeaderGroupNode } from '@/modules/companies'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay, parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import styled, { useTheme } from 'styled-components'
import { format } from '@/utils/date-fns'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './components/DietButton'
import { Card } from '@/components_deprecated/Card'
import { useWorkPeriodApprovePermissions } from './hooks'
import {
  Button,
  Combobox,
  ConditionalWrapper,
  H2,
  H4,
  Tooltip,
} from '@/components'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useSalaryApproveColumns } from './hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { SalaryDayButton } from './components/SalaryDayButton'
import { useWorkPeriodFlagColors } from './components/work-period-flags'
import { WorkPeriodAddons } from './components/work-period-addons/work-period-addons'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import { useState } from 'react'
import { WorkPeriodHoursAndMap } from './WorkPeriodHoursAndMap'
import type { ApprovalRow } from './approval-rows'
import {
  CalendarItemCommentsButton,
  CalendarItemCommentsPanel,
} from './CalendarItemCommentsButton'
import { useWorkPeriodApprovalActions } from './useWorkPeriodApprovalActions'
import { useWorkPeriodDayExtensionActions } from './use-work-period-day-extension-actions'
import { getWorkPeriodDayExtensionState } from './work-period-day-extension'
import truncate from 'lodash/truncate'
import { GRAPH_WORK_PERIOD, WORK_PERIOD } from './queries'
import { ViewWorkPeriodLogsModal } from './components/view-work-period-logs-modal'
import { WorkPeriodAllocation } from './components/work-period-allocation'
import { enforceHourMinuteString } from '@/modules/activities/util'
import { RestBandCard, RestPeriodCard } from './components/rest-row/rest-cards'

type MobileCardTab = 'standard' | 'time' | 'comments' | 'map'

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`

const EmptyCommentAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  font-style: italic;
`

const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const StyledCard = styled(Card)<{ $color?: string | null }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  background-color: ${props => props.$color ?? props.theme.colors.white};

  > h3 {
    margin: 1rem 0 0.25rem 0;
  }
  > h4 {
    margin: 1rem 0 0.5rem 0;
  }
`

const CardHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: flex-start;
`

const CardActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.theme.media.mobile} {
    display: none;
  }
`

const CardTitleContent = styled.div`
  min-width: 0;
`

const CardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

const UserName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.2;
  overflow-wrap: anywhere;
`

const DateLine = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
`

const LocationDetail = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EstimatedBadge = styled.span`
  align-self: flex-start;
  margin-top: 0.15rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.4rem;
  background: ${props => props.theme.colors.orange};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, mipnmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`

const TimeBlock = styled.div`
  min-width: 0;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
`

const PlannedTime = styled.div`
  min-height: 1rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.8rem;
  line-height: 1.2;
`

const ExtensionNote = styled.div`
  margin-top: 0.2rem;
  color: ${props => props.theme.colors.primary500};
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
`

const ActualTime = styled.div`
  margin-top: 0.2rem;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
`

const DetailPanel = styled.div`
  margin-top: 1rem;
  padding: 0 1rem;
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.gray11};
`

const DietWrapper = styled.div`
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
`

const SelectLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 600;
`

interface ApprovalWrapperProps {
  $approved: boolean | null
  $locked?: boolean
}

const ApprovalWrapper = styled.div<ApprovalWrapperProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 1rem;

  border-radius: 8px;
  background-color: ${props =>
    props.$approved
      ? props.theme.colors.green100
      : props.$approved === null
      ? 'transparent'
      : props.theme.colors.red100};
  border: 1px solid ${props => props.theme.colors.gray8};
  opacity: ${props => (props.$locked ? 0.5 : 1)};
  cursor: ${props => (props.$locked ? 'not-allowed' : 'inherit')};

  &:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray8};
  }
`

const ApprovalHalf = styled.div<{ $locked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  cursor: ${props => (props.$locked ? 'not-allowed' : 'default')};
`

const ApprovalIconButton = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`

const ApprovalCommentArea = styled.div<{ clickable?: boolean }>`
  flex: 1;
  min-width: 0;
  min-height: 1.5rem;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`

const LockedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: -0.25rem 0 0.5rem 0;
  font-size: 0.85rem;
  font-style: italic;
  color: ${props => props.theme.colors.gray3};
`

const AddonsAsTextWrapper = styled.div`
  position: relative;
  margin: 1rem 0;
  border-top: 1px solid ${props => props.theme.colors.gray8};
  padding-top: 1rem;
  min-height: 40px;
`

const MobileTabBar = styled.div`
  display: none;

  ${props => props.theme.media.mobile} {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(3rem, 1fr));
    gap: 0.5rem;
    margin-top: 1rem;
  }
`

const MobileTabButton = styled.button<{ $active: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 3rem;
  border: 1px solid
    ${props =>
      props.$active ? props.theme.colors.primary : props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props =>
    props.$active ? props.theme.colors.gray11 : props.theme.colors.white};
  color: ${props => props.theme.colors.primary};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`

const MobileTabBadge = styled.span`
  position: absolute;
  top: -0.4rem;
  right: -0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: ${props => props.theme.colors.secondary};
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
`

const ResponsivePanel = styled.div<{ $active: boolean }>`
  ${props => props.theme.media.mobile} {
    display: ${props => (props.$active ? 'block' : 'none')};
  }
  margin-top: 1rem;
`

const MobilePanel = styled.div<{ $active: boolean }>`
  display: none;

  ${props => props.theme.media.mobile} {
    display: ${props => (props.$active ? 'block' : 'none')};
    margin-top: 1rem;
  }
`

export const WorkPeriodApproveCards: React.FC<{
  approvalRows: ApprovalRow[]
  leaderGroups: LeaderGroupNode[]
  singleUserSelected?: boolean
  loading: boolean
  initRefetch: () => void
  refetchAfterApproval: () => void
}> = ({
  approvalRows,
  leaderGroups,
  singleUserSelected,
  initRefetch,
  refetchAfterApproval,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    addons: 'salary.addons',
    workHours: 'common.work-hours',
    pause: 'common.pause',
    department: 'common.department',
    facility: 'common.facility',
    departmentCount: ['common.department-expandable-text', { amount: 0 }],
    facilityCount: ['common.facility-expandable-text', { amount: 0 }],
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    estimated: 'salary.estimated',
    comment: 'common.comment',
    comments: 'common.comments',
    managerComment: 'common.manager-comment',
    map: 'common.map',
    planned: 'common.planned',
    standard: 'common.standard',
    time: 'common.time',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
    leaderGroups: 'companies.leader-groups',
    salaryMode: 'common.salary-mode',
    chooseGroup: 'common.choose-group',
    transferedOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
    lockedByManager: 'salary.locked-by-manager-approval',
    noPermissionToChange: 'salary.no-permission-to-change',
    driverMustConfirmBeforeApproval:
      'salary.driver-must-confirm-before-approval',
    commentRequiredDriverApproval: 'salary.comment-required-on-driver-approval',
    commentRequiredManagerApproval:
      'salary.comment-required-on-manager-approval',
    manualHours: 'salary.this-work-day-has-manual-hours-from-time-entries',
    placeholder: 'common.standard-for-user',
    extendWorkPeriod: 'salary.extend-work-period',
    addedBefore: 'salary.extend-work-period-added-before',
    addedAfter: 'salary.extend-work-period-added-after',
    actionLog: 'salary.work-period-log',
    ferryUsed: 'salary.work-day-includes-ferry',
    unknownVehicle: 'salary.work-day-includes-unknown-vehicle',
    onlyBlankVehicleDriving: 'salary.work-day-only-blank-vehicle-driving',
    manuallyEnteredTachoData:
      'salary.work-day-includes-manually-entered-tacho-data',
    editedAfterLock: 'salary.edited-after-lock',
    editedAfterLockAt: ['salary.edited-after-lock-at', { date: '' }],
    approveAllForWeek: 'salary.approve-all-for-week',
    approveAllForMonth: 'salary.approve-all-for-month',
    approveAllConfirm: ['salary.approve-all-confirm', { n: 0 }],
  })

  const addPrompt = usePrompt()

  const openLogs = (workPeriodId: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriodId} onClose={resolve} />
    ))

  const salaryConfiguration = useSalaryModuleConfiguration()
  const { isEnabled: isColumnEnabled } = useSalaryApproveColumns()
  const showSalaryMode = isColumnEnabled(ColumnNames.SALARY_NAME)
  const showEmployeeGroup = isColumnEnabled(ColumnNames.EMPLOYEE_GROUP)
  const showDiet = isColumnEnabled(ColumnNames.DIET)
  const showAddons = isColumnEnabled(ColumnNames.ADDONS)
  const showWorkHours = isColumnEnabled(ColumnNames.TOTAL_WORK)
  const showPause = isColumnEnabled(ColumnNames.PAUSE)
  const showDepartment = isColumnEnabled(ColumnNames.DEPARTMENT)
  const showFacility = isColumnEnabled(ColumnNames.FACILITY)
  const showApprovedByDriver =
    salaryConfiguration.driverApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_DRIVER)
  const showApprovedByManager =
    salaryConfiguration.managerApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_MANAGER)
  const showTransferredToFlex =
    salaryConfiguration.canTransferWorkToAbsence &&
    isColumnEnabled(ColumnNames.TRANSFERRED_TO_FLEX)
  const [openWorkPeriods, setOpenWorkPeriods] = useState<string[]>([])
  const [showMaps, setShowMaps] = useState(true)
  const [mobileTabs, setMobileTabs] = useState<Record<string, MobileCardTab>>(
    {}
  )

  const me = useUser()
  const theme = useTheme()
  const flagColors = useWorkPeriodFlagColors()

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()
  const { hasPermissions } = usePermissions()
  const canAddTimeEntry = hasPermissions(PERMISSIONS.timesheets.add.timeentry)
  const canChangeRestPaid = hasPermissions(
    PERMISSIONS.activities.change.driveractivitypaidstatus,
    true
  )

  const { patchWorkPeriod, transferOvertimeToFlex } = useWorkPeriodMigrations()
  const {
    openApprovalCommentFlow,
    toggleApproval,
    approveManyAsManager,
    isBulkApproving,
  } = useWorkPeriodApprovalActions({
    refetchAfterApproval,
    patchWorkPeriod,
    salaryConfiguration,
    commentRequiredDriverApproval: translations.commentRequiredDriverApproval,
    commentRequiredManagerApproval: translations.commentRequiredManagerApproval,
  })
  const { openExtendDayFlow, creatingExtension } =
    useWorkPeriodDayExtensionActions({
      initRefetch,
    })

  // Group work periods by week
  const approvalRowGroups = approvalRows.reduce(
    (acc, row) => {
      const weekStart = getWeek(parseISO(row.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(row)

      return acc
    },
    {} as Record<string, ApprovalRow[]>
  )

  // Work periods the current manager may still approve (driver-confirmed where
  // required, not yet manager-approved). Feeds the "approve all" bulk actions.
  const getManagerApprovable = (rows: ApprovalRow[]) =>
    rows
      .map(row => row.workPeriod)
      .filter((workPeriod): workPeriod is NonNullable<typeof workPeriod> =>
        Boolean(workPeriod)
      )
      .filter(workPeriod => {
        const { canApproveAsManager } = workPeriodApprovalState(
          me.id,
          workPeriod
        )
        return canApproveAsManager && !workPeriod.approvedByManager
      })

  // Only offer bulk approval to a manager who has drilled into a single driver.
  const showBulkApprove = Boolean(singleUserSelected) && showApprovedByManager
  const monthApprovable = showBulkApprove
    ? getManagerApprovable(approvalRows)
    : []

  return (
    <span>
      {showBulkApprove && monthApprovable.length > 0 && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            disabled={isBulkApproving}
            onClick={() => {
              void approveManyAsManager(
                monthApprovable,
                translations.approveAllConfirm({ n: monthApprovable.length })
              )
            }}
          >
            {translations.approveAllForMonth}
          </Button>
        </div>
      )}
      {Object.entries(approvalRowGroups).map(group => {
        const weekNumber = group[0]
        const approvalRowGroup = group[1]

        const weekApprovable = showBulkApprove
          ? getManagerApprovable(approvalRowGroup)
          : []

        return (
          <GroupWrapper key={weekNumber}>
            <div className="flex items-center justify-between gap-4">
              <H2>{translations.weekN({ n: weekNumber })}</H2>
              {weekApprovable.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBulkApproving}
                  onClick={() => {
                    void approveManyAsManager(
                      weekApprovable,
                      translations.approveAllConfirm({
                        n: weekApprovable.length,
                      })
                    )
                  }}
                >
                  {translations.approveAllForWeek}
                </Button>
              )}
            </div>
            {approvalRowGroup.map(row => {
              if (row.type === 'calendarItem') {
                const plannedStart = parseISO(row.calendarItem.start)
                const plannedEnd = parseISO(row.calendarItem.end)
                const plannedDateLines = isSameDay(plannedStart, plannedEnd)
                  ? [format(plannedStart, 'EEE dd. MMMM')]
                  : [
                      format(plannedStart, 'EEE dd. MMMM'),
                      format(plannedEnd, 'EEE dd. MMMM'),
                    ]
                const activeMobileTab = mobileTabs[row.id] ?? 'standard'
                const setActiveMobileTab = (tab: MobileCardTab) =>
                  setMobileTabs(current => ({ ...current, [row.id]: tab }))

                return (
                  <StyledCard key={row.id} $color="white">
                    <CardHeader>
                      <CardTitleContent>
                        <CardTitle>
                          <UserName>{row.user.fullName}</UserName>
                          {plannedDateLines.map(dateLine => (
                            <DateLine key={dateLine}>{dateLine}</DateLine>
                          ))}
                          <LocationDetail>
                            {row.calendarItem.title}
                          </LocationDetail>
                        </CardTitle>
                      </CardTitleContent>
                      <CardActions>
                        <CalendarItemCommentsButton
                          calendarItem={row.calendarItem}
                        />
                      </CardActions>
                    </CardHeader>
                    <TimeGrid>
                      <TimeBlock>
                        <PlannedTime>{translations.planned}</PlannedTime>
                        <ActualTime>{format(plannedStart, 'HH:mm')}</ActualTime>
                      </TimeBlock>
                      <TimeBlock>
                        <PlannedTime>{translations.planned}</PlannedTime>
                        <ActualTime>{format(plannedEnd, 'HH:mm')}</ActualTime>
                      </TimeBlock>
                    </TimeGrid>
                    <MobileTabBar>
                      <MobileTabButton
                        type="button"
                        $active={activeMobileTab === 'standard'}
                        title={translations.standard}
                        aria-label={translations.standard}
                        onClick={() => setActiveMobileTab('standard')}
                      >
                        <Icon icon="check" color={theme.colors.primary} />
                      </MobileTabButton>
                      <MobileTabButton
                        type="button"
                        $active={activeMobileTab === 'comments'}
                        title={translations.comments}
                        aria-label={translations.comments}
                        onClick={() => setActiveMobileTab('comments')}
                      >
                        <Icon icon="comment" color={theme.colors.primary} />
                        {row.calendarItem.numComments > 0 && (
                          <MobileTabBadge>
                            {row.calendarItem.numComments}
                          </MobileTabBadge>
                        )}
                      </MobileTabButton>
                    </MobileTabBar>
                    {activeMobileTab === 'comments' && (
                      <MobilePanel $active>
                        <CalendarItemCommentsPanel
                          calendarItem={row.calendarItem}
                          me={me}
                          inline
                        />
                      </MobilePanel>
                    )}
                  </StyledCard>
                )
              }

              if (row.type === 'restPeriod') {
                const restCanEdit =
                  !row.restPeriod.workPeriodBefore.approvedByManager
                return row.restPeriod.isWeekRest ? (
                  <RestPeriodCard
                    key={row.id}
                    restPeriod={row.restPeriod}
                    userId={row.user.id}
                    canEdit={restCanEdit}
                    showSalaryMode={showSalaryMode}
                    showDiet={showDiet}
                    showApproval={showApprovedByDriver || showApprovedByManager}
                    onChange={initRefetch}
                  />
                ) : (
                  <RestBandCard
                    key={row.id}
                    restPeriod={row.restPeriod}
                    canChangePaid={canChangeRestPaid && restCanEdit}
                    onChange={initRefetch}
                  />
                )
              }

              const workPeriod = row.workPeriod
              const {
                driverApproveDirty,
                managerApproveDirty,
                canApproveAsDriver,
                canApproveAsManager,
                managerBlockedByDriverConfirmation,
              } = workPeriodApprovalState(me.id, workPeriod)
              const driverLocked =
                !canApproveAsDriver && workPeriod.approvedByManager
              const managerBlockedReason = managerBlockedByDriverConfirmation
                ? translations.driverMustConfirmBeforeApproval
                : translations.noPermissionToChange
              const leaderGroupItems = [
                ...leaderGroups.map(group => ({
                  value: group.id,
                  label: group.name,
                })),
              ]
              if (
                workPeriod.leaderGroup &&
                !leaderGroupItems.some(
                  item => item.value === workPeriod.leaderGroup?.id
                )
              ) {
                leaderGroupItems.unshift({
                  value: workPeriod.leaderGroup.id,
                  label: workPeriod.leaderGroup.name,
                })
              }

              const isOpen = openWorkPeriods.includes(workPeriod.id)
              let color: string | null = 'white'
              if (
                !workPeriod.approvedByUser &&
                salaryConfiguration.driverApprove &&
                workPeriod.userComment
              ) {
                color = theme.colors.red100
              }
              if (
                !workPeriod.approvedByManager &&
                salaryConfiguration.managerApprove &&
                workPeriod.managerComment
              ) {
                color = theme.colors.red100
              }
              if (workPeriod.approvedByManager) {
                color = theme.colors.green100
              }
              if (
                !salaryConfiguration.driverApprove &&
                !salaryConfiguration.managerApprove
              ) {
                color = null
              }
              // Edited after lock takes precedence — flag the card orange.
              if (workPeriod.editedAfterLock) {
                color = '#ffeccc'
              }

              const workPeriodStart = parseISO(workPeriod.datetimeStart)
              const workPeriodEnd = parseISO(workPeriod.datetimeEnd)
              const extensionState = getWorkPeriodDayExtensionState({
                workPeriodStart,
                workPeriodEnd,
                timeEntries: workPeriod.timeEntries,
              })
              const plannedStart = workPeriod.calendarItem?.start
                ? format(parseISO(workPeriod.calendarItem.start), 'HH:mm')
                : null
              const plannedEnd = workPeriod.calendarItem?.end
                ? format(parseISO(workPeriod.calendarItem.end), 'HH:mm')
                : null
              const workPeriodDateLines = isSameDay(
                workPeriodStart,
                workPeriodEnd
              )
                ? [format(workPeriodStart, 'EEE dd. MMMM')]
                : [
                    format(workPeriodStart, 'EEE dd. MMMM'),
                    format(workPeriodEnd, 'EEE dd. MMMM'),
                  ]
              const startLocation = shortenLocationDetail(
                workPeriod.startLocationDetail
              )
              const endLocation = shortenLocationDetail(
                workPeriod.endLocationDetail
              )
              const canToggleFlexTransfer =
                !!workPeriod.absenceHourTransaction ||
                (salaryConfiguration.canTransferWorkToAbsence &&
                  !!workPeriod.salarySetting?.useDayOvertime &&
                  !workPeriod.salarySetting.useWeekOvertime)
              const canExtendDay =
                canAddTimeEntry &&
                !workPeriod.approvedByManager &&
                !!workPeriod.user.salaryWorkTime &&
                (canApproveAsDriver || canApproveAsManager)
              const activeMobileTab = mobileTabs[workPeriod.id] ?? 'standard'
              const setActiveMobileTab = (tab: MobileCardTab) =>
                setMobileTabs(current => ({
                  ...current,
                  [workPeriod.id]: tab,
                }))
              const activitiesOnly = workPeriod.salarySetting
                ? workPeriod.salarySetting.salaryType !== 'HOUR' &&
                  (workPeriod.salaryDay?.partOfDay ?? 0) > 0
                : false

              return (
                <StyledCard key={workPeriod.id} $color={color}>
                  <CardHeader>
                    <CardTitleContent>
                      <CardTitle>
                        <UserName>{workPeriod.user.fullName}</UserName>
                        {workPeriodDateLines.map(dateLine => (
                          <DateLine key={dateLine}>
                            {workPeriod.hasTimeEntries && (
                              <Tooltip content={translations.manualHours}>
                                <span className="mr-1">
                                  <Icon
                                    icon="pen-to-square"
                                    color={flagColors.medium}
                                  />
                                </span>
                              </Tooltip>
                            )}
                            {dateLine}
                          </DateLine>
                        ))}
                        {workPeriod.editedAfterLock && (
                          <DateLine>
                            <span className="mr-1">
                              <Icon
                                icon="exclamation-triangle"
                                color={flagColors.high}
                              />
                            </span>
                            {workPeriod.lockedAt
                              ? translations.editedAfterLockAt({
                                  date: format(
                                    parseISO(workPeriod.lockedAt),
                                    'dd.MM.yyyy HH:mm'
                                  ),
                                })
                              : translations.editedAfterLock}
                          </DateLine>
                        )}
                        {workPeriod.hasFerryConditions && (
                          <DateLine>
                            <span className="mr-1">
                              <Icon icon="ferry" color={flagColors.low} />
                            </span>
                            {translations.ferryUsed}
                          </DateLine>
                        )}
                        {workPeriod.hasUnknownVehicle && (
                          <DateLine>
                            <span className="mr-1">
                              <Icon icon="truck" color={flagColors.high} />
                            </span>
                            {translations.unknownVehicle}
                          </DateLine>
                        )}
                        {workPeriod.hasOnlyBlankVehicleDriving && (
                          <DateLine>
                            <span className="mr-1">
                              <Icon
                                icon="truck-front"
                                color={flagColors.medium}
                              />
                            </span>
                            {translations.onlyBlankVehicleDriving}
                          </DateLine>
                        )}
                        {workPeriod.hasManuallyEnteredActivities && (
                          <DateLine>
                            <span className="mr-1">
                              <Icon icon="m" color={flagColors.medium} />
                            </span>
                            {translations.manuallyEnteredTachoData}
                          </DateLine>
                        )}
                        {workPeriod.isEstimated && (
                          <EstimatedBadge>
                            {translations.estimated}
                          </EstimatedBadge>
                        )}
                      </CardTitle>
                    </CardTitleContent>
                    <CardActions>
                      {workPeriod.salarySetting && (
                        <Button
                          size="icon-lg"
                          variant="outline"
                          onClick={() => {
                            if (isOpen) {
                              setOpenWorkPeriods(
                                openWorkPeriods.filter(
                                  id => id !== workPeriod.id
                                )
                              )
                            } else {
                              setOpenWorkPeriods([
                                ...openWorkPeriods,
                                workPeriod.id,
                              ])
                            }
                          }}
                        >
                          <Icon
                            icon="clock"
                            color={theme.colors.primary}
                            size="30"
                          />
                        </Button>
                      )}
                      {canExtendDay && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={creatingExtension}
                          onClick={() => {
                            void openExtendDayFlow(workPeriod)
                          }}
                        >
                          {translations.extendWorkPeriod}
                        </Button>
                      )}
                      {workPeriod.calendarItem && (
                        <CalendarItemCommentsButton
                          calendarItem={workPeriod.calendarItem}
                        />
                      )}
                      <Button
                        size="icon-lg"
                        variant="outline"
                        title={translations.actionLog}
                        aria-label={translations.actionLog}
                        onClick={() => openLogs(workPeriod.id)}
                      >
                        <Icon
                          icon="clock-rotate-left"
                          color={theme.colors.primary}
                          size="24"
                        />
                      </Button>
                    </CardActions>
                  </CardHeader>
                  <TimeGrid>
                    <TimeBlock>
                      {plannedStart && (
                        <PlannedTime>
                          {translations.planned} {plannedStart}
                        </PlannedTime>
                      )}
                      <ActualTime>
                        {format(workPeriodStart, 'HH:mm')}
                      </ActualTime>
                      {extensionState.beforeExtensionStart && (
                        <ExtensionNote>
                          {translations.addedBefore}{' '}
                          {format(extensionState.beforeExtensionStart, 'HH:mm')}
                        </ExtensionNote>
                      )}
                      {startLocation && (
                        <LocationDetail>{startLocation}</LocationDetail>
                      )}
                    </TimeBlock>
                    <TimeBlock>
                      {plannedEnd && (
                        <PlannedTime>
                          {translations.planned} {plannedEnd}
                        </PlannedTime>
                      )}
                      <ActualTime>{format(workPeriodEnd, 'HH:mm')}</ActualTime>
                      {extensionState.afterExtensionEnd && (
                        <ExtensionNote>
                          {translations.addedAfter}{' '}
                          {format(extensionState.afterExtensionEnd, 'HH:mm')}
                        </ExtensionNote>
                      )}
                      {endLocation && (
                        <LocationDetail>{endLocation}</LocationDetail>
                      )}
                    </TimeBlock>
                  </TimeGrid>
                  <MobileTabBar>
                    <MobileTabButton
                      type="button"
                      $active={activeMobileTab === 'standard'}
                      title={translations.standard}
                      aria-label={translations.standard}
                      onClick={() => setActiveMobileTab('standard')}
                    >
                      <Icon icon="check" color={theme.colors.primary} />
                    </MobileTabButton>
                    {workPeriod.salarySetting && (
                      <MobileTabButton
                        type="button"
                        $active={activeMobileTab === 'time'}
                        title={translations.time}
                        aria-label={translations.time}
                        onClick={() => setActiveMobileTab('time')}
                      >
                        <Icon icon="clock" color={theme.colors.primary} />
                      </MobileTabButton>
                    )}
                    {canExtendDay && (
                      <MobileTabButton
                        type="button"
                        $active={false}
                        title={translations.extendWorkPeriod}
                        aria-label={translations.extendWorkPeriod}
                        disabled={creatingExtension}
                        onClick={() => {
                          void openExtendDayFlow(workPeriod)
                        }}
                      >
                        <Icon icon="edit" color={theme.colors.primary} />
                      </MobileTabButton>
                    )}
                    {workPeriod.calendarItem && (
                      <MobileTabButton
                        type="button"
                        $active={activeMobileTab === 'comments'}
                        title={translations.comments}
                        aria-label={translations.comments}
                        onClick={() => setActiveMobileTab('comments')}
                      >
                        <Icon icon="comment" color={theme.colors.primary} />
                        {workPeriod.calendarItem.numComments > 0 && (
                          <MobileTabBadge>
                            {workPeriod.calendarItem.numComments}
                          </MobileTabBadge>
                        )}
                      </MobileTabButton>
                    )}
                    <MobileTabButton
                      type="button"
                      $active={activeMobileTab === 'map'}
                      title={translations.map}
                      aria-label={translations.map}
                      onClick={() => setActiveMobileTab('map')}
                    >
                      <Icon icon="map" color={theme.colors.primary} />
                    </MobileTabButton>
                    <MobileTabButton
                      type="button"
                      $active={false}
                      title={translations.actionLog}
                      aria-label={translations.actionLog}
                      onClick={() => openLogs(workPeriod.id)}
                    >
                      <Icon
                        icon="clock-rotate-left"
                        color={theme.colors.primary}
                      />
                    </MobileTabButton>
                  </MobileTabBar>
                  <ResponsivePanel $active={activeMobileTab === 'standard'}>
                    {showApprovedByDriver && (
                      <>
                        <h4>{translations.approvedByUser}</h4>
                        <ConditionalWrapper
                          condition={driverLocked}
                          wrapper={c => (
                            <Tooltip content={translations.lockedByManager}>
                              <span>{c}</span>
                            </Tooltip>
                          )}
                        >
                          <ApprovalWrapper
                            $approved={
                              workPeriod.approvedByUser
                                ? true
                                : !driverApproveDirty
                                ? null
                                : false
                            }
                            $locked={!canApproveAsDriver}
                          >
                            <ApprovalHalf $locked={!canApproveAsDriver}>
                              <ApprovalIconButton
                                disabled={!canApproveAsDriver}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsDriver) return
                                  void toggleApproval(workPeriod, 'driver')
                                }}
                              >
                                <Checkmark
                                  checked={workPeriod.approvedByUser}
                                  icon={
                                    !driverApproveDirty
                                      ? driverLocked
                                        ? 'lock'
                                        : 'plus-circle'
                                      : undefined
                                  }
                                  color={
                                    !driverApproveDirty
                                      ? theme.colors.gray3
                                      : undefined
                                  }
                                />
                              </ApprovalIconButton>
                              <ApprovalCommentArea
                                clickable={canApproveAsDriver}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsDriver) return
                                  void openApprovalCommentFlow(
                                    workPeriod,
                                    'driver'
                                  )
                                }}
                              >
                                {workPeriod.userComment && (
                                  <CommentText>
                                    {workPeriod.userComment}
                                  </CommentText>
                                )}
                                {!workPeriod.userComment &&
                                  canApproveAsDriver && (
                                    <EmptyCommentAction>
                                      <Icon icon="comment" />
                                      {translations.comment}
                                    </EmptyCommentAction>
                                  )}
                                {workPeriod.approvedByUser &&
                                  workPeriod.userApprover && (
                                    <div className="mt-0.5 text-xs text-gray-500">
                                      {workPeriod.userApprover.fullName}
                                    </div>
                                  )}
                              </ApprovalCommentArea>
                            </ApprovalHalf>
                          </ApprovalWrapper>
                        </ConditionalWrapper>
                        {driverLocked && (
                          <LockedNotice>
                            <Icon icon="lock" />
                            {translations.lockedByManager}
                          </LockedNotice>
                        )}
                      </>
                    )}

                    {showApprovedByManager && (
                      <>
                        <h4>{translations.approvedByManager}</h4>
                        <ConditionalWrapper
                          condition={!canApproveAsManager}
                          wrapper={c => (
                            <Tooltip content={managerBlockedReason}>
                              <span>{c}</span>
                            </Tooltip>
                          )}
                        >
                          <ApprovalWrapper
                            $approved={
                              workPeriod.approvedByManager
                                ? true
                                : !managerApproveDirty
                                ? null
                                : false
                            }
                            $locked={!canApproveAsManager}
                          >
                            <ApprovalHalf $locked={!canApproveAsManager}>
                              <ApprovalIconButton
                                disabled={!canApproveAsManager}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsManager) return
                                  void toggleApproval(workPeriod, 'manager')
                                }}
                              >
                                <Checkmark
                                  checked={workPeriod.approvedByManager}
                                  icon={
                                    !managerApproveDirty
                                      ? !canApproveAsManager
                                        ? 'lock'
                                        : 'plus-circle'
                                      : undefined
                                  }
                                  color={
                                    !managerApproveDirty
                                      ? theme.colors.gray3
                                      : undefined
                                  }
                                />
                              </ApprovalIconButton>
                              <ApprovalCommentArea
                                clickable={canApproveAsManager}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsManager) return
                                  void openApprovalCommentFlow(
                                    workPeriod,
                                    'manager'
                                  )
                                }}
                              >
                                {workPeriod.managerComment && (
                                  <CommentText>
                                    {workPeriod.managerComment}
                                  </CommentText>
                                )}
                                {!workPeriod.managerComment &&
                                  canApproveAsManager && (
                                    <EmptyCommentAction>
                                      <Icon icon="comment" />
                                      {translations.comment}
                                    </EmptyCommentAction>
                                  )}
                                {workPeriod.approvedByManager &&
                                  workPeriod.managerApprover && (
                                    <div className="mt-0.5 text-xs text-gray-500">
                                      {workPeriod.managerApprover.fullName}
                                    </div>
                                  )}
                              </ApprovalCommentArea>
                            </ApprovalHalf>
                          </ApprovalWrapper>
                        </ConditionalWrapper>
                        {!canApproveAsManager && (
                          <LockedNotice>
                            <Icon icon="lock" />
                            {managerBlockedReason}
                          </LockedNotice>
                        )}
                      </>
                    )}
                    <hr className="my-4 border-gray-200 dark:border-gray-700" />
                    <DietWrapper>
                      {showEmployeeGroup &&
                        (leaderGroupItems.length > 0 ||
                          workPeriod.leaderGroup) && (
                          <>
                            <SelectLabel>
                              {translations.leaderGroups}
                            </SelectLabel>
                            <Combobox
                              mode="single"
                              value={workPeriod.leaderGroup?.id ?? ''}
                              items={leaderGroupItems}
                              clearable
                              disabled={workPeriod.approvedByManager}
                              placeholder={translations.chooseGroup}
                              onChange={async value => {
                                if (workPeriod.approvedByManager) return
                                await patchWorkPeriod({
                                  variables: {
                                    id: workPeriod.id,
                                    input: {
                                      leaderGroup: value || null,
                                    },
                                  },
                                })
                                initRefetch()
                              }}
                            />
                          </>
                        )}
                      {showSalaryMode && (
                        <>
                          <SelectLabel>{translations.salaryMode}</SelectLabel>
                          <SalarySettingSelect
                            value={workPeriod.salarySetting?.id || null}
                            disabled={workPeriod.approvedByManager}
                            placeholder={translations.placeholder}
                            onChange={async value => {
                              if (workPeriod.approvedByManager) return
                              await patchWorkPeriod({
                                variables: {
                                  id: workPeriod.id,
                                  input: {
                                    salarySetting: value === '' ? null : value,
                                  },
                                },
                              })
                              initRefetch()
                            }}
                          />
                        </>
                      )}
                      {showSalaryMode &&
                        workPeriod.salarySetting?.salaryType == 'DAY' && (
                          <SalaryDayButton
                            workPeriod={workPeriod}
                            salaryDay={workPeriod.salaryDay}
                            userId={workPeriod.user.id}
                            canEdit={
                              !workPeriod.approvedByManager &&
                              (canApproveAsManager ||
                                !workPeriod.approvedByUser)
                            }
                            border={true}
                            onChange={() => initRefetch()}
                            restrictWidth
                          />
                        )}
                      {showDiet && (
                        <DietButton
                          workPeriod={workPeriod}
                          userId={workPeriod.user.id}
                          canEdit={!workPeriod.approvedByManager}
                          border={true}
                          onChange={() => initRefetch()}
                          restrictWidth
                        />
                      )}
                      {showTransferredToFlex && (
                        <div className="flex w-full items-center justify-between mt-2 px-4">
                          <div className="min-w-[105px]">
                            {translations.transferedOvertimeToFlextime}
                          </div>
                          <Icon
                            icon={
                              workPeriod.absenceHourTransaction
                                ? 'check'
                                : 'times'
                            }
                            color={
                              workPeriod.absenceHourTransaction
                                ? theme.colors.green
                                : undefined
                            }
                            onClick={() => {
                              if (!canToggleFlexTransfer) return
                              void (async () => {
                                const result = await transferOvertimeToFlex({
                                  variables: {
                                    workPeriod: workPeriod.id,
                                  },
                                  refetchQueries: [
                                    {
                                      query: WORK_PERIOD,
                                      variables: { id: workPeriod.id },
                                    },
                                    {
                                      query: GRAPH_WORK_PERIOD,
                                      variables: { id: workPeriod.id },
                                    },
                                  ],
                                  awaitRefetchQueries: true,
                                })
                                if (
                                  result.data
                                    ?.transferOvertimeWorkToAbsenceHourTransaction
                                    .success
                                ) {
                                  await initRefetch()
                                }
                              })()
                            }}
                          />
                        </div>
                      )}
                    </DietWrapper>
                    {(showWorkHours ||
                      showPause ||
                      showDepartment ||
                      showFacility) && (
                      <dl className="mt-4 flex flex-col gap-2 border-t border-stone-200 pt-4">
                        {showWorkHours && (
                          <div className="flex items-center justify-between gap-3">
                            <dt>{translations.workHours}</dt>
                            <dd className="font-mono">
                              {enforceHourMinuteString(
                                workPeriod.activityTimes?.timeWorkTotal
                              )}
                            </dd>
                          </div>
                        )}
                        {showPause && (
                          <div className="flex items-center justify-between gap-3">
                            <dt>{translations.pause}</dt>
                            <dd className="font-mono">
                              {enforceHourMinuteString(
                                workPeriod.activityTimes?.timePause
                              )}
                            </dd>
                          </div>
                        )}
                        {showDepartment && (
                          <div className="flex items-center justify-between gap-3">
                            <dt>{translations.department}</dt>
                            <dd className="min-w-0">
                              <WorkPeriodAllocation
                                title={translations.department}
                                items={(workPeriod.departments ?? []).map(
                                  ({ department, duration }) => ({
                                    ...department,
                                    duration,
                                  })
                                )}
                                countLabel={amount =>
                                  translations.departmentCount({ amount })
                                }
                                datetimeStart={workPeriodStart}
                                datetimeEnd={workPeriodEnd}
                              />
                            </dd>
                          </div>
                        )}
                        {showFacility && (
                          <div className="flex items-center justify-between gap-3">
                            <dt>{translations.facility}</dt>
                            <dd className="min-w-0">
                              <WorkPeriodAllocation
                                title={translations.facility}
                                items={(workPeriod.facilities ?? []).map(
                                  ({ facility, duration }) => ({
                                    ...facility,
                                    duration,
                                  })
                                )}
                                countLabel={amount =>
                                  translations.facilityCount({ amount })
                                }
                                datetimeStart={workPeriodStart}
                                datetimeEnd={workPeriodEnd}
                              />
                            </dd>
                          </div>
                        )}
                      </dl>
                    )}
                    {showAddons && (
                      <AddonsAsTextWrapper>
                        <H4>{translations.addons}</H4>
                        <WorkPeriodAddons
                          userId={workPeriod.user.id}
                          datetimeStart={workPeriodStart}
                          datetimeEnd={workPeriodEnd}
                          addonsAsText={workPeriod.addonsAsText}
                          canEdit={!workPeriod.approvedByManager}
                          lockedLabel={translations.lockedByManager}
                          fullWidth
                          onChange={() => initRefetch()}
                        />
                      </AddonsAsTextWrapper>
                    )}
                    {workPeriod.salarySetting && isOpen && (
                      <DetailPanel>
                        <WorkPeriodHoursAndMap
                          workPeriodId={workPeriod.id}
                          activitiesOnly={activitiesOnly}
                          showMap={showMaps}
                          onToggleMap={() => setShowMaps(value => !value)}
                        />
                      </DetailPanel>
                    )}
                  </ResponsivePanel>
                  {workPeriod.salarySetting && activeMobileTab === 'time' && (
                    <MobilePanel $active>
                      <WorkPeriodHoursAndMap
                        workPeriodId={workPeriod.id}
                        activitiesOnly={activitiesOnly}
                        showMap={false}
                        onToggleMap={() => setShowMaps(value => !value)}
                        view="time"
                      />
                    </MobilePanel>
                  )}
                  {workPeriod.calendarItem &&
                    activeMobileTab === 'comments' && (
                      <MobilePanel $active>
                        <CalendarItemCommentsPanel
                          calendarItem={workPeriod.calendarItem}
                          me={me}
                          inline
                        />
                      </MobilePanel>
                    )}
                  {activeMobileTab === 'map' && (
                    <MobilePanel $active>
                      <WorkPeriodHoursAndMap
                        workPeriodId={workPeriod.id}
                        activitiesOnly={activitiesOnly}
                        showMap={true}
                        onToggleMap={() => setShowMaps(value => !value)}
                        view="map"
                      />
                    </MobilePanel>
                  )}
                </StyledCard>
              )
            })}
          </GroupWrapper>
        )
      })}
    </span>
  )
}

type LocationDetailInfo = {
  description?: string | null
  postalCode?: string | null
  postalCity?: string | null
}

function shortenLocationDetail(location?: LocationDetailInfo | null) {
  if (!location) return null

  const description = truncate(
    location.description
      ?.split(',')
      .map(part => part.trim())
      .filter(Boolean)[0] ?? '',
    { length: 25 }
  )
  const place = [location.postalCode, location.postalCity]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')

  return [description, place].filter(Boolean).join(', ') || null
}
